import React, { useState, useEffect, useCallback } from 'react';
import { Search, Package, Calendar, FileText, AlertCircle, User, FileCheck } from 'lucide-react';
import { pickingService } from '../../../../services/pickingService';
import { useAuth } from '../../../../contexts/AuthContext';
import { useTranslation } from '../../../../contexts/I18nProvider';
import {
  CompletedPickListDto,
  CompletedPickListDetailDto,
  CompletedPickListLineDto,
  PListOrderDto
} from '../../../../types/completedPickingList';
import { Card } from '../../../common/Card';
import { BackButton } from '../../../common/BackButton';

type TabType = 'lines' | 'packages' | 'info';

interface CompletedPickingListsProps {
  onBack?: () => void;
}

export const CompletedPickingLists: React.FC<CompletedPickingListsProps> = ({ onBack }) => {
  const { t } = useTranslation();
  const { user } = useAuth();

  // State for master list
  const [pickLists, setPickLists] = useState<CompletedPickListDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // State for filters
  const [searchTerm, setSearchTerm] = useState('');
  const [dateFrom, setDateFrom] = useState<string>('');
  const [dateTo, setDateTo] = useState<string>('');

  // State for detail view
  const [selectedPListId, setSelectedPListId] = useState<number | null>(null);
  const [detailData, setDetailData] = useState<CompletedPickListDetailDto | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<TabType>('lines');

  // Initialize date filters (default 7 days back)
  useEffect(() => {
    const today = new Date();
    const sevenDaysAgo = new Date();
    sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);

    setDateTo(today.toISOString().split('T')[0]);
    setDateFrom(sevenDaysAgo.toISOString().split('T')[0]);
  }, []);

  // Load completed pick lists
  const loadPickLists = useCallback(async () => {
    if (!dateFrom || !dateTo) return;

    try {
      setLoading(true);
      setError(null);

      const fromDate = new Date(dateFrom);
      const toDate = new Date(dateTo);
      toDate.setHours(23, 59, 59, 999); // Include entire day

      const lists = await pickingService.getCompletedPickLists(fromDate, toDate);
      setPickLists(lists);
    } catch (err) {
      const error = err as Error;
      setError(error.message || t('warehouse.modules.history.completedPickingLists.errors.loadFailed'));
      console.error('Error loading completed pick lists:', err);
    } finally {
      setLoading(false);
    }
  }, [dateFrom, dateTo]);

  // Load pick lists when dates change
  useEffect(() => {
    if (dateFrom && dateTo) {
      loadPickLists();
    }
  }, [dateFrom, dateTo, loadPickLists]);

  // Load detail data when a pick list is selected
  const loadDetailData = useCallback(async (pListId: number) => {
    try {
      setDetailLoading(true);
      setDetailError(null);

      const detail = await pickingService.getCompletedPickListDetail(pListId);

      if (!detail) {
        setDetailError(t('warehouse.modules.history.completedPickingLists.errors.notFound'));
        return;
      }

      setDetailData(detail);
    } catch (err) {
      const error = err as Error;
      setDetailError(error.message || t('warehouse.modules.history.completedPickingLists.errors.loadDetailsFailed'));
      console.error('Error loading pick list detail:', err);
    } finally {
      setDetailLoading(false);
    }
  }, []);

  // Handle pick list selection
  const handleSelectPickList = useCallback((pListId: number) => {
    setSelectedPListId(pListId);
    setActiveTab('lines'); // Reset to first tab
    loadDetailData(pListId);
  }, [loadDetailData]);

  // Filter pick lists based on search term
  const filteredPickLists = pickLists.filter(list => {
    const searchLower = searchTerm.toLowerCase();
    return (
      list.pListID.toString().includes(searchLower) ||
      list.description1?.toLowerCase().includes(searchLower) ||
      list.description2?.toLowerCase().includes(searchLower) ||
      list.operatorID?.toLowerCase().includes(searchLower) ||
      list.carrierName?.toLowerCase().includes(searchLower)
    );
  });

  // Render master list (left side)
  const renderMasterList = () => (
    <div className="flex flex-col h-full">
      {/* Search box */}
      <div className="mb-4">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
          <input
            type="text"
            placeholder={t('warehouse.modules.history.completedPickingLists.searchPlaceholder')}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
      </div>

      {/* List of pick lists */}
      <div className="flex-1 overflow-y-auto space-y-2">
        {loading && (
          <div className="flex items-center justify-center py-8">
            <div className="w-6 h-6 animate-spin border-2 border-blue-500 border-t-transparent rounded-full" />
            <span className="ml-2 text-sm text-gray-600">{t('common.status.loading')}</span>
          </div>
        )}

        {error && (
          <div className="flex items-start gap-2 p-3 bg-red-50 border border-red-200 rounded-lg">
            <AlertCircle className="text-red-600 flex-shrink-0 mt-0.5" size={18} />
            <div className="text-sm text-red-800">{error}</div>
          </div>
        )}

        {!loading && !error && filteredPickLists.length === 0 && (
          <div className="text-center py-8 text-gray-500">
            <Package size={48} className="mx-auto mb-2 text-gray-300" />
            <p className="text-sm">{t('warehouse.modules.history.completedPickingLists.noListsFound')}</p>
          </div>
        )}

        {!loading && !error && filteredPickLists.map((list) => (
          <Card
            key={list.pListID}
            onClick={() => handleSelectPickList(list.pListID)}
            className={`p-3 transition-all ${
              selectedPListId === list.pListID
                ? 'border-blue-500 border-2 bg-blue-50'
                : 'hover:border-gray-300'
            }`}
          >
            <div className="flex items-start justify-between">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <FileCheck size={16} className="text-green-600 flex-shrink-0" />
                  <span className="font-semibold text-gray-900">PL {list.pListID}</span>
                  {(list.hasSignature1 === 1 || list.hasSignature2 === 1) && (
                    <span title={t('warehouse.modules.history.completedPickingLists.hasSignature')}>
                      <FileText size={14} className="text-blue-600" />
                    </span>
                  )}
                </div>

                <div className="text-xs text-gray-600 space-y-0.5">
                  <div className="flex items-center gap-1">
                    <Calendar size={12} className="flex-shrink-0" />
                    <span>{pickingService.formatDate(list.pListDate)}</span>
                  </div>

                  {list.operatorID && (
                    <div className="flex items-center gap-1">
                      <User size={12} className="flex-shrink-0" />
                      <span>{list.operatorID}</span>
                    </div>
                  )}

                  {list.description1 && (
                    <div className="truncate text-gray-500" title={list.description1}>
                      {list.description1}
                    </div>
                  )}
                </div>
              </div>

              {list.numOfOrderLines && list.numOfOrderLines > 0 && (
                <div className="ml-2 flex-shrink-0">
                  <div className="text-xs font-medium text-gray-600">
                    {list.numOfOrderLines} {t('warehouse.modules.history.completedPickingLists.lines')}
                  </div>
                </div>
              )}
            </div>
          </Card>
        ))}
      </div>
    </div>
  );

  // Render detail view (right side)
  const renderDetailView = () => {
    if (!selectedPListId) {
      return (
        <div className="flex items-center justify-center h-full text-gray-400">
          <div className="text-center">
            <Package size={64} className="mx-auto mb-4 text-gray-300" />
            <p>{t('warehouse.modules.history.completedPickingLists.selectListToViewDetails')}</p>
          </div>
        </div>
      );
    }

    if (detailLoading) {
      return (
        <div className="flex items-center justify-center h-full">
          <div className="w-8 h-8 animate-spin border-2 border-blue-500 border-t-transparent rounded-full" />
          <span className="ml-3 text-gray-600">{t('warehouse.modules.history.completedPickingLists.loadingDetails')}</span>
        </div>
      );
    }

    if (detailError) {
      return (
        <div className="flex items-center justify-center h-full">
          <div className="text-center text-red-600">
            <AlertCircle size={48} className="mx-auto mb-4" />
            <p>{detailError}</p>
          </div>
        </div>
      );
    }

    if (!detailData) {
      return null;
    }

    const { pickList, orderLines, packages } = detailData;

    return (
      <div className="flex flex-col h-full">
        {/* Header info */}
        <div className="mb-4 p-4 bg-white rounded-lg border border-gray-200">
          <h2 className="text-xl font-bold text-gray-900 mb-3">
            {t('warehouse.modules.history.completedPickingLists.pickingList')} {pickList.pListID}
          </h2>

          <div className="grid grid-cols-2 md:grid-cols-3 gap-x-6 gap-y-2 text-sm">
            <div>
              <span className="text-gray-500">{t('common.fields.status')}:</span>
              <span className="ml-2 font-medium text-green-600">
                {pickingService.getStatusText(pickList.pListStatus)}
              </span>
            </div>

            {pickList.pListDate && (
              <div>
                <span className="text-gray-500">{t('common.fields.date')}:</span>
                <span className="ml-2 font-medium">
                  {pickingService.formatDateTime(pickList.pListDate)}
                </span>
              </div>
            )}

            {pickList.operatorID && (
              <div>
                <span className="text-gray-500">{t('warehouse.general.operator')}:</span>
                <span className="ml-2 font-medium">{pickList.operatorID}</span>
              </div>
            )}

            {pickList.deliveryDate && (
              <div>
                <span className="text-gray-500">{t('warehouse.modules.history.completedPickingLists.deliveryDate')}:</span>
                <span className="ml-2 font-medium">
                  {pickingService.formatDate(pickList.deliveryDate)}
                </span>
              </div>
            )}

            {pickList.carrierName && (
              <div>
                <span className="text-gray-500">{t('warehouse.modules.history.completedPickingLists.carrier')}:</span>
                <span className="ml-2 font-medium">{pickList.carrierName}</span>
              </div>
            )}

            {pickList.numOfPackages !== undefined && pickList.numOfPackages > 0 && (
              <div>
                <span className="text-gray-500">{t('warehouse.modules.history.completedPickingLists.numberOfPackages')}:</span>
                <span className="ml-2 font-medium">{pickList.numOfPackages}</span>
              </div>
            )}
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-4 border-b border-gray-200">
          <button
            onClick={() => setActiveTab('lines')}
            className={`px-4 py-2 font-medium transition-colors ${
              activeTab === 'lines'
                ? 'text-blue-600 border-b-2 border-blue-600'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            {t('warehouse.modules.history.completedPickingLists.orderLines')} ({orderLines.length})
          </button>
          <button
            onClick={() => setActiveTab('packages')}
            className={`px-4 py-2 font-medium transition-colors ${
              activeTab === 'packages'
                ? 'text-blue-600 border-b-2 border-blue-600'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            {t('warehouse.modules.history.completedPickingLists.packages')} ({packages.length})
          </button>
          <button
            onClick={() => setActiveTab('info')}
            className={`px-4 py-2 font-medium transition-colors ${
              activeTab === 'info'
                ? 'text-blue-600 border-b-2 border-blue-600'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            {t('warehouse.modules.history.completedPickingLists.info')}
          </button>
        </div>

        {/* Tab content */}
        <div className="flex-1 overflow-y-auto">
          {activeTab === 'lines' && renderOrderLinesTab(orderLines)}
          {activeTab === 'packages' && renderPackagesTab(packages)}
          {activeTab === 'info' && renderInfoTab(pickList)}
        </div>
      </div>
    );
  };

  // Render order lines tab
  const renderOrderLinesTab = (lines: CompletedPickListLineDto[]) => {
    if (lines.length === 0) {
      return (
        <div className="text-center py-8 text-gray-500">
          <FileText size={48} className="mx-auto mb-2 text-gray-300" />
          <p>{t('warehouse.modules.history.completedPickingLists.noOrderLinesFound')}</p>
        </div>
      );
    }

    return (
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 sticky top-0">
            <tr>
              <th className="px-3 py-2 text-left font-medium text-gray-700">{t('warehouse.general.itemNumber')}</th>
              <th className="px-3 py-2 text-left font-medium text-gray-700">{t('common.fields.description')}</th>
              <th className="px-3 py-2 text-right font-medium text-gray-700">{t('warehouse.modules.history.completedPickingLists.quantityPicked')}</th>
              <th className="px-3 py-2 text-left font-medium text-gray-700">{t('warehouse.modules.history.completedPickingLists.unit')}</th>
              <th className="px-3 py-2 text-left font-medium text-gray-700">{t('warehouse.modules.history.completedPickingLists.fromLocation')}</th>
              <th className="px-3 py-2 text-left font-medium text-gray-700">{t('warehouse.modules.history.completedPickingLists.pickedBy')}</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {lines.map((line, index) => (
              <tr key={`${line.orderNo}-${line.lineNo}-${index}`} className="hover:bg-gray-50">
                <td className="px-3 py-2 font-medium text-gray-900">{line.itemNo}</td>
                <td className="px-3 py-2 text-gray-700">
                  <div className="max-w-xs truncate" title={line.description1 || undefined}>
                    {line.description1 || '-'}
                  </div>
                </td>
                <td className="px-3 py-2 text-right font-medium text-gray-900">
                  {line.pListLineQtyPicked ?? line.quantityPicked ?? '-'}
                </td>
                <td className="px-3 py-2 text-gray-700">{line.itemUnitCode || '-'}</td>
                <td className="px-3 py-2 text-gray-700">
                  {line.pickedFromBinCode || line.stdLocationName || '-'}
                </td>
                <td className="px-3 py-2 text-gray-700">{line.pickedBy || '-'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  // Render packages tab
  const renderPackagesTab = (packages: PListOrderDto[]) => {
    if (packages.length === 0) {
      return (
        <div className="text-center py-8 text-gray-500">
          <Package size={48} className="mx-auto mb-2 text-gray-300" />
          <p>{t('warehouse.modules.history.completedPickingLists.noPackageInfoFound')}</p>
        </div>
      );
    }

    return (
      <div className="space-y-4">
        {packages.map((pkg, index) => (
          <Card key={`${pkg.pListID}-${pkg.orderNo}-${index}`} className="p-4">
            <div className="grid grid-cols-2 md:grid-cols-3 gap-x-6 gap-y-3 text-sm">
              <div>
                <span className="text-gray-500">{t('warehouse.modules.history.completedPickingLists.orderNumber')}:</span>
                <span className="ml-2 font-medium">{pkg.orderNo}</span>
              </div>

              {pkg.packageNo !== undefined && (
                <div>
                  <span className="text-gray-500">{t('warehouse.modules.history.completedPickingLists.packageNumber')}:</span>
                  <span className="ml-2 font-medium">{pkg.packageNo}</span>
                </div>
              )}

              {pkg.numOfPackages !== undefined && pkg.numOfPackages > 0 && (
                <div>
                  <span className="text-gray-500">{t('warehouse.modules.history.completedPickingLists.numberOfPackages')}:</span>
                  <span className="ml-2 font-medium">{pkg.numOfPackages}</span>
                </div>
              )}

              {pkg.totalWeight !== undefined && pkg.totalWeight > 0 && (
                <div>
                  <span className="text-gray-500">{t('warehouse.modules.history.completedPickingLists.weight')}:</span>
                  <span className="ml-2 font-medium">{pkg.totalWeight.toFixed(2)} kg</span>
                </div>
              )}

              {pkg.totalVolume !== undefined && pkg.totalVolume > 0 && (
                <div>
                  <span className="text-gray-500">{t('warehouse.modules.history.completedPickingLists.volume')}:</span>
                  <span className="ml-2 font-medium">{pkg.totalVolume.toFixed(2)} m³</span>
                </div>
              )}

              {pkg.carrierName && (
                <div>
                  <span className="text-gray-500">{t('warehouse.modules.history.completedPickingLists.carrier')}:</span>
                  <span className="ml-2 font-medium">{pkg.carrierName}</span>
                </div>
              )}

              {pkg.signature1DateTime && (
                <div className="col-span-2 md:col-span-3">
                  <div className="flex items-start gap-2 p-2 bg-green-50 rounded border border-green-200">
                    <FileCheck size={16} className="text-green-600 flex-shrink-0 mt-0.5" />
                    <div>
                      <div className="font-medium text-green-900">{t('warehouse.modules.history.completedPickingLists.signature1')}</div>
                      <div className="text-xs text-green-700">
                        {pickingService.formatDateTime(pkg.signature1DateTime)}
                        {pkg.signature1Location && ` - ${pkg.signature1Location}`}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {pkg.signature2DateTime && (
                <div className="col-span-2 md:col-span-3">
                  <div className="flex items-start gap-2 p-2 bg-blue-50 rounded border border-blue-200">
                    <FileCheck size={16} className="text-blue-600 flex-shrink-0 mt-0.5" />
                    <div>
                      <div className="font-medium text-blue-900">{t('warehouse.modules.history.completedPickingLists.signature2')}</div>
                      <div className="text-xs text-blue-700">
                        {pickingService.formatDateTime(pkg.signature2DateTime)}
                        {pkg.signature2Location && ` - ${pkg.signature2Location}`}
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </Card>
        ))}
      </div>
    );
  };

  // Render info tab
  const renderInfoTab = (pickList: CompletedPickListDto) => (
    <Card className="p-6">
      <h3 className="text-lg font-semibold text-gray-900 mb-4">{t('warehouse.modules.history.completedPickingLists.pickingListInformation')}</h3>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-4 text-sm">
        <div>
          <span className="text-gray-500">{t('warehouse.modules.history.completedPickingLists.pickingListId')}:</span>
          <span className="ml-2 font-medium">{pickList.pListID}</span>
        </div>

        <div>
          <span className="text-gray-500">{t('warehouse.modules.history.completedPickingLists.companyNumber')}:</span>
          <span className="ml-2 font-medium">{pickList.companyNo}</span>
        </div>

        {pickList.description1 && (
          <div className="md:col-span-2">
            <span className="text-gray-500">{t('warehouse.modules.history.completedPickingLists.description1')}:</span>
            <span className="ml-2 font-medium">{pickList.description1}</span>
          </div>
        )}

        {pickList.description2 && (
          <div className="md:col-span-2">
            <span className="text-gray-500">{t('warehouse.modules.history.completedPickingLists.description2')}:</span>
            <span className="ml-2 font-medium">{pickList.description2}</span>
          </div>
        )}

        <div>
          <span className="text-gray-500">Status:</span>
          <span className="ml-2 font-medium">{pickingService.getStatusText(pickList.pListStatus)}</span>
        </div>

        {pickList.priority !== undefined && (
          <div>
            <span className="text-gray-500">{t('warehouse.modules.history.completedPickingLists.priority')}:</span>
            <span className="ml-2 font-medium">{pickList.priority}</span>
          </div>
        )}

        {pickList.startWorkDateTime && (
          <div>
            <span className="text-gray-500">{t('warehouse.modules.history.completedPickingLists.startWork')}:</span>
            <span className="ml-2 font-medium">
              {pickingService.formatDateTime(pickList.startWorkDateTime)}
            </span>
          </div>
        )}

        {pickList.stopWorkDateTime && (
          <div>
            <span className="text-gray-500">{t('warehouse.modules.history.completedPickingLists.stopWork')}:</span>
            <span className="ml-2 font-medium">
              {pickingService.formatDateTime(pickList.stopWorkDateTime)}
            </span>
          </div>
        )}

        {pickList.deliveryMethodNo !== undefined && (
          <div>
            <span className="text-gray-500">{t('warehouse.modules.history.completedPickingLists.deliveryMethod')}:</span>
            <span className="ml-2 font-medium">{pickList.deliveryMethodNo}</span>
          </div>
        )}

        {pickList.packZoneID && (
          <div>
            <span className="text-gray-500">{t('warehouse.modules.history.completedPickingLists.packZone')}:</span>
            <span className="ml-2 font-medium">{pickList.packZoneID}</span>
          </div>
        )}

        {pickList.packageRemark && (
          <div className="md:col-span-2">
            <span className="text-gray-500">{t('warehouse.modules.history.completedPickingLists.packageRemarks')}:</span>
            <span className="ml-2 font-medium">{pickList.packageRemark}</span>
          </div>
        )}

        {pickList.numOfOrderLinesPicked !== undefined && pickList.numOfOrderLines !== undefined && (
          <div>
            <span className="text-gray-500">{t('warehouse.modules.history.completedPickingLists.linesPicked')}:</span>
            <span className="ml-2 font-medium">
              {pickList.numOfOrderLinesPicked} / {pickList.numOfOrderLines}
            </span>
          </div>
        )}

        {pickList.numOfPallets !== undefined && pickList.numOfPallets > 0 && (
          <div>
            <span className="text-gray-500">{t('warehouse.modules.history.completedPickingLists.numberOfPallets')}:</span>
            <span className="ml-2 font-medium">{pickList.numOfPallets}</span>
          </div>
        )}

        {pickList.totalWeight !== undefined && pickList.totalWeight > 0 && (
          <div>
            <span className="text-gray-500">{t('warehouse.modules.history.completedPickingLists.totalWeight')}:</span>
            <span className="ml-2 font-medium">{pickList.totalWeight.toFixed(2)} kg</span>
          </div>
        )}

        {pickList.totalVolume !== undefined && pickList.totalVolume > 0 && (
          <div>
            <span className="text-gray-500">{t('warehouse.modules.history.completedPickingLists.totalVolume')}:</span>
            <span className="ml-2 font-medium">{pickList.totalVolume.toFixed(2)} m³</span>
          </div>
        )}

        <div>
          <span className="text-gray-500">{t('warehouse.modules.history.completedPickingLists.signatures')}:</span>
          <span className="ml-2">
            {pickList.hasSignature1 === 1 && (
              <span className="inline-flex items-center gap-1 text-green-600">
                <FileCheck size={14} /> {t('warehouse.modules.history.completedPickingLists.signature1')}
              </span>
            )}
            {pickList.hasSignature1 === 1 && pickList.hasSignature2 === 1 && (
              <span className="mx-1 text-gray-400">|</span>
            )}
            {pickList.hasSignature2 === 1 && (
              <span className="inline-flex items-center gap-1 text-blue-600">
                <FileCheck size={14} /> {t('warehouse.modules.history.completedPickingLists.signature2')}
              </span>
            )}
            {pickList.hasSignature1 === 0 && pickList.hasSignature2 === 0 && (
              <span className="text-gray-400">{t('warehouse.modules.history.completedPickingLists.noSignatures')}</span>
            )}
          </span>
        </div>

        {pickList.createdBy && (
          <div>
            <span className="text-gray-500">{t('common.fields.createdBy')}:</span>
            <span className="ml-2 font-medium">{pickList.createdBy}</span>
          </div>
        )}

        {pickList.created && (
          <div>
            <span className="text-gray-500">{t('common.fields.createdAt')}:</span>
            <span className="ml-2 font-medium">
              {pickingService.formatDateTime(pickList.created)}
            </span>
          </div>
        )}
      </div>
    </Card>
  );

  return (
    <div className="min-h-screen bg-gray-50 p-4">
      <div className="max-w-7xl mx-auto">
        {/* Header with filters */}
        <div className="bg-white rounded-lg shadow-sm p-4 mb-6">
          <div className="flex items-center gap-3 mb-4">
            <BackButton onClick={onBack || (() => {})} />
            <h1 className="text-2xl font-bold text-gray-900">{t('warehouse.modules.history.completedPickingLists.title')}</h1>
          </div>

          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('warehouse.modules.history.completedPickingLists.fromDate')}
              </label>
              <input
                type="date"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            <div className="flex-1">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('warehouse.modules.history.completedPickingLists.toDate')}
              </label>
              <input
                type="date"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            <div className="flex items-end">
              <button
                onClick={loadPickLists}
                disabled={loading || !dateFrom || !dateTo}
                className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
              >
                {t('common.buttons.search')}
              </button>
            </div>
          </div>
        </div>

        {/* Master-Detail Layout */}
        <div className="grid grid-cols-1 md:grid-cols-5 gap-6">
          {/* Master List (40% on desktop) */}
          <div className="md:col-span-2">
            <Card className="p-4 h-[calc(100vh-16rem)]">
              {renderMasterList()}
            </Card>
          </div>

          {/* Detail View (60% on desktop) */}
          <div className="md:col-span-3">
            <Card className="p-4 h-[calc(100vh-16rem)]">
              {renderDetailView()}
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};
